import { type PlaybackErrorCode } from '../core/errors';
import {
  type AudioTrackInfo,
  type PlayerState,
  type QualityLevel,
  type TextTrackInfo,
} from '../core/stats';

/** postMessage protocol version. Bump on a breaking change; sent in `oddin:ready`. */
export const PROTOCOL_VERSION = 1;

/** Commands the host page sends down to the iframe. */
export type HostToFrame =
  | { type: 'oddin:play' }
  | { type: 'oddin:pause' }
  | { type: 'oddin:setMuted'; muted: boolean }
  | { type: 'oddin:setVolume'; volume: number }
  | { type: 'oddin:setQuality'; index: number } // -1 = auto
  | { type: 'oddin:setMaxBitrate'; bitrate: number | null }
  | { type: 'oddin:setAudioTrack'; id: number }
  | { type: 'oddin:setTextTrack'; id: number } // -1 = off
  | { type: 'oddin:seekToLive' }
  | { type: 'oddin:enterPip' }
  | { type: 'oddin:exitPip' }
  | { type: 'oddin:retry' }
  | { type: 'oddin:load'; matchUrn: string }
  | { type: 'oddin:destroy' };

/** Events the iframe sends up to the host page. */
export type FrameToHost =
  | { type: 'oddin:ready'; protocol: number }
  | { type: 'oddin:autoplayblocked' }
  | { type: 'oddin:state'; state: PlayerState }
  | { type: 'oddin:waiting'; retryInMs: number; attempt: number; phase: string }
  | {
      type: 'oddin:tracks';
      quality: QualityLevel[];
      audio: AudioTrackInfo[];
      text: TextTrackInfo[];
    }
  | { type: 'oddin:qualitychange'; index: number }
  | { type: 'oddin:audiotrackchange'; id: number }
  | { type: 'oddin:texttrackchange'; id: number }
  | {
      type: 'oddin:stats';
      droppedFrames: number;
      latencySeconds?: number;
      bandwidthKbps?: number;
      levelHeight?: number;
      lowLatency?: boolean;
      isLive?: boolean;
      atLiveEdge?: boolean;
    }
  | { type: 'oddin:error'; code: PlaybackErrorCode; message: string };

export function isOddinMessage(data: unknown): data is { type: string } {
  return (
    !!data &&
    typeof data === 'object' &&
    typeof (data as { type?: unknown }).type === 'string' &&
    (data as { type: string }).type.startsWith('oddin:')
  );
}
